import Phaser from 'phaser';
import {
  COLORS,
  DEPTHS,
  DUNNING_MODAL,
  FONTS,
  GAME_HEIGHT,
  GAME_WIDTH,
  toHexColor,
} from '../config/gameConfig';
import { COPY } from '../content/copy';
import type { DunningTemplate, EventBus } from '../systems/EventBus';
import { renderHelpLink } from './HelpLink';
import { SEED_INVOICES, agingBucketOf } from '../content/seed-invoices';
import type { ARLedger } from '../systems/ARLedger';
import type { Tooltip } from './Tooltip';
import { propagateDepth } from './propagateDepth';

type TemplateKey = 'firstNotice' | 'pastDue' | 'finalNotice' | 'collectionsReferral';

const TEMPLATE_ORDER: readonly { key: TemplateKey; id: DunningTemplate }[] = [
  { key: 'firstNotice', id: 'first-notice' },
  { key: 'pastDue', id: 'past-due' },
  { key: 'finalNotice', id: 'final-notice' },
  { key: 'collectionsReferral', id: 'collections-referral' },
];

const CORRECT_BY_BUCKET: Record<string, ReadonlySet<DunningTemplate>> = {
  '0-30': new Set(['first-notice']),
  '31-60': new Set(['past-due']),
  '61-90': new Set(['final-notice']),
  '90+': new Set(['final-notice', 'collections-referral']),
};

export class DunningModal {
  private container: Phaser.GameObjects.Container | null = null;
  private escHandler: ((ev: KeyboardEvent) => void) | null = null;
  private currentCustomer = 'Acme Corp';
  private currentBucket = '90+';

  private readonly onBeat = (p: { beatId: string }) => {
    if (p.beatId === 'beat-2-dunning' && !this.container) {
      this.currentCustomer = 'Acme Corp';
      this.currentBucket = '90+';
      this.open();
    }
  };

  private readonly onOpenFor = (p: { customer: string; bucket: string }) => {
    if (this.container) return;
    this.currentCustomer = p.customer;
    this.currentBucket = p.bucket;
    this.open();
  };

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly bus: EventBus,
    private readonly tooltip?: Tooltip,
    private readonly ledger?: ARLedger,
  ) {
    bus.on('beat:triggered', this.onBeat);
    bus.on('dunning:open-for', this.onOpenFor);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    scene.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());
  }

  pickTemplateForTest(template: DunningTemplate): void {
    this.emitSent(template);
    this.close();
  }

  private open(): void {
    this.bus.emit('term:unlocked', { term: 'dunning' });
    this.bus.emit('dunning:opened', { customer: this.currentCustomer });

    const x = Math.round((GAME_WIDTH - DUNNING_MODAL.width) / 2);
    const y = Math.round((GAME_HEIGHT - DUNNING_MODAL.height) / 2);

    const root = this.scene.add.container(0, 0);
    root.setDepth(DEPTHS.modal);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, COLORS.black, DUNNING_MODAL.backdropAlpha)
      .setOrigin(0, 0);
    root.add(backdrop);

    const panel = this.scene.add
      .rectangle(x, y, DUNNING_MODAL.width, DUNNING_MODAL.height, COLORS.windowBody)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLORS.windowBorder)
      .setInteractive();
    root.add(panel);

    const title = this.scene.add.text(
      x + DUNNING_MODAL.paddingX,
      y + DUNNING_MODAL.paddingY,
      `Send dunning — ${this.currentCustomer}`,
      {
        fontFamily: FONTS.ui,
        fontSize: DUNNING_MODAL.titleFontSize,
        color: toHexColor(COLORS.textPrimary),
        fontStyle: 'bold',
      },
    );
    root.add(title);

    renderHelpLink(
      this.scene,
      root,
      x + DUNNING_MODAL.width - DUNNING_MODAL.paddingX,
      y + DUNNING_MODAL.paddingY + 4,
      'dunning',
      this.bus,
    );

    // Compute the customer's open balance + oldest age dynamically so the
    // prompt always reflects the player's actual lookup data — not a
    // hardcoded Acme line.
    const allInvoices = this.ledger ? this.ledger.current : SEED_INVOICES;
    const open = allInvoices.filter((i) => i.customer === this.currentCustomer);
    const totalOpen = open.reduce((s, i) => s + i.amount, 0);
    const oldestDays = open.reduce((m, i) => Math.max(m, i.agingDays), 0);
    const inBucket = open.filter((i) => agingBucketOf(i.agingDays) === this.currentBucket);
    const bucketTotal = inBucket.reduce((s, i) => s + i.amount, 0);
    const fmtUsd = (n: number) => `$${n.toLocaleString()}`;
    const dynamicPrompt = open.length === 0
      ? `${this.currentCustomer} — bucket ${this.currentBucket}.`
      : `${this.currentCustomer} — ${fmtUsd(bucketTotal)} in ${this.currentBucket} (${inBucket.length} invoice${inBucket.length === 1 ? '' : 's'}). Total open: ${fmtUsd(totalOpen)}, oldest ${oldestDays}d.`;

    const promptY = y + DUNNING_MODAL.paddingY + title.height + 8;
    const prompt = this.scene.add.text(
      x + DUNNING_MODAL.paddingX,
      promptY,
      dynamicPrompt,
      {
        fontFamily: FONTS.ui,
        fontSize: DUNNING_MODAL.promptFontSize,
        color: toHexColor(COLORS.accentDanger),
        wordWrap: { width: DUNNING_MODAL.width - DUNNING_MODAL.paddingX * 2 },
      },
    );
    root.add(prompt);

    const subpromptY = promptY + prompt.height + 6;
    const subprompt = this.scene.add.text(
      x + DUNNING_MODAL.paddingX,
      subpromptY,
      COPY.dunning.subprompt,
      {
        fontFamily: FONTS.ui,
        fontSize: DUNNING_MODAL.subpromptFontSize,
        color: toHexColor(COLORS.textMuted),
      },
    );
    root.add(subprompt);

    let rowY = subpromptY + subprompt.height + 10;
    for (const { key, id } of TEMPLATE_ORDER) {
      const row = this.scene.add
        .rectangle(
          x + DUNNING_MODAL.paddingX,
          rowY,
          DUNNING_MODAL.width - DUNNING_MODAL.paddingX * 2,
          DUNNING_MODAL.templateRowHeight,
          COLORS.windowChrome,
        )
        .setOrigin(0, 0)
        .setStrokeStyle(1, COLORS.windowBorder)
        .setInteractive({ useHandCursor: true });
      root.add(row);

      const copy = COPY.dunning.templates[key];
      const label = this.scene.add.text(
        x + DUNNING_MODAL.paddingX + 12,
        rowY + 10,
        copy.label,
        {
          fontFamily: FONTS.ui,
          fontSize: DUNNING_MODAL.templateLabelFontSize,
          color: toHexColor(COLORS.textPrimary),
          fontStyle: 'bold',
        },
      );
      root.add(label);

      const blurb = this.scene.add.text(
        x + DUNNING_MODAL.paddingX + 12,
        rowY + 32,
        copy.blurb,
        {
          fontFamily: FONTS.ui,
          fontSize: DUNNING_MODAL.templateBlurbFontSize,
          color: toHexColor(COLORS.textMuted),
        },
      );
      root.add(blurb);

      row.on('pointerdown', () => {
        this.emitSent(id);
        this.showToast(copy.label);
        this.close();
      });

      rowY += DUNNING_MODAL.templateRowHeight + DUNNING_MODAL.templateRowGap;
    }

    const lastRowBottomY = rowY - DUNNING_MODAL.templateRowGap;

    const cancel = this.scene.add
      .text(
        x + DUNNING_MODAL.width - DUNNING_MODAL.paddingX,
        y + DUNNING_MODAL.height - DUNNING_MODAL.paddingY,
        COPY.dunning.cancelButton,
        {
          fontFamily: FONTS.ui,
          fontSize: DUNNING_MODAL.cancelButtonFontSize,
          color: toHexColor(COLORS.textMuted),
        },
      )
      .setOrigin(1, 1);
    const defaultCancelY = y + DUNNING_MODAL.height - DUNNING_MODAL.paddingY;
    const minCancelY = lastRowBottomY + cancel.height + 8;
    const maxCancelY = y + DUNNING_MODAL.height - 6;
    cancel.y = Math.min(maxCancelY, Math.max(defaultCancelY, minCancelY));
    cancel.setInteractive({ useHandCursor: true });
    cancel.on('pointerdown', () => this.close());
    root.add(cancel);

    this.escHandler = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') this.close();
    };
    this.scene.input.keyboard?.on('keydown', this.escHandler);

    this.container = root;
    propagateDepth(root, DEPTHS.modal);
  }

  private emitSent(template: DunningTemplate): void {
    const correctSet = CORRECT_BY_BUCKET[this.currentBucket] ?? CORRECT_BY_BUCKET['90+'];
    this.bus.emit('dunning:sent', {
      customer: this.currentCustomer,
      template,
      correctForBucket: correctSet.has(template),
    });
  }

  private showToast(templateLabel: string): void {
    const toast = this.scene.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT - 80,
      `${COPY.dunning.sentToastPrefix} ${templateLabel}`,
      {
        fontFamily: FONTS.ui,
        fontSize: DUNNING_MODAL.toastFontSize,
        color: toHexColor(COLORS.textPrimary),
        backgroundColor: toHexColor(COLORS.windowChrome),
        padding: { x: 12, y: 8 },
      },
    ).setOrigin(0.5, 0.5).setDepth(DEPTHS.modal);

    this.scene.time.delayedCall(DUNNING_MODAL.toastDurationMs, () => toast.destroy());
  }

  private close(): void {
    if (this.escHandler) {
      this.scene.input.keyboard?.off('keydown', this.escHandler);
      this.escHandler = null;
    }
    if (this.container) {
      this.container.destroy();
      this.container = null;
    }
  }

  private teardown(): void {
    this.bus.off('beat:triggered', this.onBeat);
    this.bus.off('dunning:open-for', this.onOpenFor);
    this.close();
  }
}
